/**
 * Employee Avatar Component with Lazy Loading
 * Optimized with loading="lazy" attribute and placeholder images
 */

import React, { useState, memo } from 'react'

interface EmployeeAvatarProps {
  src?: string
  name: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const SIZE_CLASSES = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-16 h-16 text-base',
  xl: 'w-20 h-20 text-lg'
}

/**
 * Memoized avatar component with lazy loading and fallback
 */
export const EmployeeAvatar = memo<EmployeeAvatarProps>(
  ({ src, name, size = 'sm' }) => {
    const [imageError, setImageError] = useState(false)
    const [imageLoaded, setImageLoaded] = useState(false)

    // Generate initials from name
    const initials = name
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()

    // Show placeholder if no src or if image failed to load
    if (!src || imageError) {
      return (
        <div
          className={`${SIZE_CLASSES[size]} rounded-full bg-brand-primary flex items-center justify-center text-brand-dark font-semibold`}
          aria-label={name}
        >
          {initials}
        </div>
      )
    }

    return (
      <div className="relative">
        {/* Placeholder shown while loading */}
        {!imageLoaded && (
          <div
            className={`${SIZE_CLASSES[size]} rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse`}
            aria-hidden="true"
          />
        )}

        {/* Actual image with lazy loading */}
        <img
          src={src}
          alt={name}
          loading="lazy"
          className={`${SIZE_CLASSES[size]} rounded-full object-cover transition-opacity ${
            imageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      </div>
    )
  },
  // Only re-render if src or name changes
  (prevProps, nextProps) => {
    return prevProps.src === nextProps.src &&
           prevProps.name === nextProps.name &&
           prevProps.size === nextProps.size
  }
)

EmployeeAvatar.displayName = 'EmployeeAvatar'
